import {useState} from "react";
import {Carousel, message} from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import RectangleCountdownTimer from "../RectangleCountdownTimer";
import TicketHero from "./TicketHero";
import EarningCard from "./EarningCard";
import TicketsModal from "./TicketsModal";
import {
    formatCampaignDate,
    getActiveEarning,
    getNextReveal,
    getTimerDate,
    getViewablePhase,
} from "../../config/campaignPhases";
import {describeRepeatingGoal} from "./rewards";
import {CAROUSEL_PACING} from "../../config/carousel";
import "./happyPayDashboard.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DashBoardBodyHappyPay = ({props, auth}) => {
    // `lotteryCount`/`eligible` from the API are not read here: the hero shows
    // the potential total (computed below) and derives eligibility from the
    // per-route checklist, so both stay consistent with the cards on screen.
    const {transactionsCount, referralsCount, cardLinked, promocodeLinked} = props;

    // Rules for the phase the server computed lotteryCount under, so the cards
    // explain the total the customer is actually looking at.
    const {transactions: txRule, referrals: refRule, cardLinked: cardRule, promoCode: promoRule} = getActiveEarning();

    // A route worth zero tickets is not offered this phase, so its card is
    // hidden. One rule for every route rather than a promo-specific flag.
    const offers = (rule) => rule?.tickets > 0;

    const [isLotteryModalOpen, setIsLotteryModalOpen] = useState(false);
    const [lotteryModalLoading, setLotteryModalLoading] = useState(false);
    const [lotteries, setLotteries] = useState(null);

    const fetchLotteries = async () => {
        const response = await axios.get(`${BASE_URL}/happypay/campaigns/happy-pay-cust.php/lotteries`, {
            params: auth
        });
        if (response.status === 200) {
            setLotteries(response.data);
        }
    };

    const handleLotteryModalOpen = async () => {
        setLotteryModalLoading(true);

        try {
            if (lotteries !== null) {
                setIsLotteryModalOpen(true);
                return;
            }

            // One question instead of two: is there a phase whose draw has
            // happened and whose window is still open? The old pair of checks
            // round-tripped a phase number through the string `iteration${n}`
            // to look the phase back up.
            if (!getViewablePhase()) {
                const nextReveal = getNextReveal();
                message.warning(nextReveal
                    ? `Урамшууллын эрх нь хараахан бэлэн болоогүй байгаа тул та ${formatCampaignDate(nextReveal)}-с хойш харна уу.`
                    : "Одоогоор уг урамшуулал нь зарлагдаагүй байна.");
                return;
            }

            await fetchLotteries();
            setIsLotteryModalOpen(true);
        } finally {
            setLotteryModalLoading(false);
        }
    };

    const transactions = describeRepeatingGoal({
        count: transactionsCount,
        interval: txRule.per,
        pointsPerInterval: txRule.tickets,
        unit: 'гүйлгээ',
        action: 'хийвэл',
    });

    const referrals = describeRepeatingGoal({
        count: referralsCount,
        interval: refRule.per,
        pointsPerInterval: refRule.tickets,
        unit: 'найз',
        action: 'уривал',
    });

    // Every offered route is required — earning tickets from only one or two of
    // them is not enough to enter the draw. `met` mirrors the backend's
    // isEligible (a full bundle, not merely any activity), so the hero's
    // checkmarks match what actually gates the draw. Promo is left out: it is
    // retired (tickets: 0), so offers(promoRule) is already false.
    const requirements = [
        offers(txRule) && {
            label: `Happy Pay-р ${txRule.per} гүйлгээ хийх`,
            met: transactionsCount >= txRule.per,
            valueText: `${transactionsCount} / ${txRule.per}`,
        },
        offers(refRule) && {
            label: `${refRule.per} найз урих`,
            met: referralsCount >= refRule.per,
            valueText: `${referralsCount} / ${refRule.per}`,
        },
        offers(cardRule) && {
            label: 'Visa карт холбох',
            met: cardLinked,
            valueText: cardLinked ? 'Холбосон' : 'Холбоогүй',
        },
    ].filter(Boolean);

    // The potential total — what the routes have accumulated — shown in the
    // hero regardless of eligibility. The backend gates the real draw count to
    // zero until every condition is met; the hero shows the number the customer
    // stands to keep, and the checklist explains it isn't in the draw yet.
    const potentialCount =
        (offers(txRule) ? transactions.earned : 0) +
        (offers(refRule) ? referrals.earned : 0) +
        (offers(cardRule) && cardLinked ? cardRule.tickets : 0) +
        (offers(promoRule) && promocodeLinked ? promoRule.tickets : 0);

    return (
        <div className="hp">
            <TicketHero
                count={potentialCount}
                requirements={requirements}
                loading={lotteryModalLoading}
                onViewTickets={handleLotteryModalOpen}
            />

            <section className="hp-grid">
                {offers(txRule) && <EarningCard
                    label="Happy Pay-р хийсэн гүйлгээ"
                    value={transactionsCount}
                    unit="ширхэг"
                    earned={transactions.earned}
                    progress={transactions.progress}
                    nextHint={transactions.nextHint}
                    pendingHint={`${txRule.per} гүйлгээ хийвэл +${txRule.tickets} эрх нэмэгдэнэ`}
                />}

                {offers(refRule) && <EarningCard
                    label="Урьсан найз"
                    value={referralsCount}
                    unit="хүн"
                    earned={referrals.earned}
                    progress={referrals.progress}
                    nextHint={referrals.nextHint}
                    pendingHint={`${refRule.per} найзаа уривал +${refRule.tickets} эрх нэмэгдэнэ`}
                />}

                {offers(cardRule) && <EarningCard
                    label="Visa карт"
                    value={cardLinked ? 'Холбосон' : 'Холбоогүй'}
                    earned={cardLinked ? cardRule.tickets : 0}
                    nextHint="Visa картаа амжилттай холбосон"
                    pendingHint={`Visa картаа холбовол +${cardRule.tickets} эрх нэмэгдэнэ`}
                />}

                {offers(promoRule) && <EarningCard
                    label="Промо код"
                    value={promocodeLinked ? 'Оруулсан' : 'Оруулаагүй'}
                    earned={promocodeLinked ? promoRule.tickets : 0}
                    nextHint="Промо кодоо амжилттай оруулсан"
                    pendingHint={`Промо код оруулбал +${promoRule.tickets} эрх нэмэгдэнэ`}
                />}
            </section>

            <section className="hp-secondary">
                {/* Countdown logic and dates left exactly as they were. */}
                <article className="hp-card hp-countdown">
                    <h3 className="hp-label">Дараагийн шалгаруулалт</h3>
                    {/* No marginTop prop: the component spreads extra props into an
                        inline style, and an inline margin-top would outrank the
                        `margin: auto` that centres the timer in its card. */}
                    <RectangleCountdownTimer targetDate={getTimerDate()}/>
                </article>

                <article className="hp-card hp-promo">
                    <Carousel autoplay dots {...CAROUSEL_PACING}>
                        <div>
                            {/* Landscape asset for wide slots, portrait for narrow.
                                See happypay/README.md for the ratios these expect. */}
                            {/* 481px, not 861px: between those widths the card is
                                still wide enough that the 1.4:1 portrait asset
                                would be cropped to a third of its height. The
                                slot takes each asset's own ratio — see the
                                aspect-ratio rules in happyPayDashboard.css. */}
                            <picture>
                                <source media="(min-width: 481px)" srcSet="/happy_pay_2026/3000x1110-2.jpg"/>
                                <img src="/happy_pay_2026/1500x1070.jpg" alt="Happy Pay урамшуулал"/>
                            </picture>
                        </div>
                    </Carousel>
                </article>
            </section>

            <TicketsModal
                open={isLotteryModalOpen}
                lotteries={lotteries}
                onClose={() => setIsLotteryModalOpen(false)}
            />
        </div>
    );
};

DashBoardBodyHappyPay.propTypes = {
    props: PropTypes.shape({
        lotteryCount: PropTypes.number,
        transactionsCount: PropTypes.number,
        referralsCount: PropTypes.number,
        cardLinked: PropTypes.bool,
        promocodeLinked: PropTypes.bool,
        eligible: PropTypes.bool,
    }).isRequired,
    auth: PropTypes.object,
};

export default DashBoardBodyHappyPay;
